// Équipe d'un projet = rôles de l'organisation dont le focus mentionne le projet (« @[project:12|Nom] »), comme sur
// l'ancienne fiche. Ces fonctions ne font que du calcul : la fiche les branche sur /circle-focus.

const ownerIds = (circle) => {
  const ids = new Set();
  if (circle?.owner_id != null) ids.add(Number(circle.owner_id));
  let list = circle?.owner_ids;
  if (typeof list === 'string') {
    try { list = JSON.parse(list); } catch { list = []; }
  }
  (Array.isArray(list) ? list : []).forEach((id) => { if (!Number.isNaN(Number(id))) ids.add(Number(id)); });
  return ids;
};

const childrenOf = (circles, parentId) => circles.filter((c) => c && String(c.parent_id) === String(parentId));

/** Cercle dont les rôles peuvent viser le projet : celui du projet, ou son parent s'il est lui-même un rôle. */
export const focusParent = (project, circles = []) => {
  if (project?.circle_id == null) return null;
  const circle = circles.find((c) => Number(c.id) === Number(project.circle_id));
  if (!circle) return Number(project.circle_id);
  const isRole = circle.parent_id != null && !childrenOf(circles, circle.id).length;
  return isRole ? Number(circle.parent_id) : Number(circle.id);
};

/** Rôles proposés (les rôles sans sous-cercle du cercle parent), par nom. */
export const roleOptions = (parentId, circles = []) => (parentId == null ? [] : childrenOf(circles, parentId)
  .filter((c) => !childrenOf(circles, c.id).length)
  .sort((a, b) => (a.name || '').localeCompare(b.name || '')));

/** Personnes qui tiennent un rôle (premiers liens), proposables pour ce projet. */
export const roleMembers = (role, employees = []) => {
  const ids = ownerIds(role);
  return employees.filter((e) => ids.has(Number(e.id))).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};

const token = (projectId) => `@[project:${projectId}|`;

/** Rôles qui visent le projet, avec leurs membres. */
export const projectRoles = (projectId, focusEntries = [], circles = [], employees = []) => {
  const groups = new Map();
  focusEntries.forEach((entry) => {
    if (!String(entry?.focus_text || '').includes(token(projectId))) return;
    const role = circles.find((c) => Number(c.id) === Number(entry.circle_id));
    const group = groups.get(entry.circle_id) || {
      roleId: entry.circle_id, role: role?.name || `Rôle ${entry.circle_id}`, members: [],
      reserved: Array.isArray(role?.reserved_domains) ? role.reserved_domains : [],
    };
    const employee = employees.find((e) => Number(e.id) === Number(entry.employee_id));
    group.members.push({ id: entry.employee_id, name: employee?.name || `#${entry.employee_id}`, picture: employee?.picture });
    groups.set(entry.circle_id, group);
  });
  return [...groups.values()].sort((a, b) => a.role.localeCompare(b.role));
};

/** Focus après ajout de la mention du projet (inchangé si elle y est déjà). */
export const withMention = (text, project) => {
  const current = String(text || '').trim();
  if (current.includes(token(project.id))) return current;
  const mention = `${token(project.id)}${String(project.name || 'Projet').replace(/\]/g, ')')}]`;
  return current ? `${current}\n${mention}` : mention;
};

/** Focus après retrait de la mention du projet (null s'il ne reste rien). */
export const withoutMention = (text, projectId) => {
  const cleaned = String(text || '').replace(new RegExp(`@\\[project:${projectId}\\|[^\\]]*\\]`, 'g'), '')
    .split(/\r?\n/).map((line) => line.replace(/\s{2,}/g, ' ').trim()).filter(Boolean).join('\n');
  return cleaned || null;
};

/** Cercles où l'utilisateur peut ranger le projet : ceux qu'il mène au-dessus du cercle actuel, et leurs descendants. */
export const circleChoices = (project, circles = [], userIds = []) => {
  const mine = new Set(userIds.filter((id) => id != null).map(Number));
  const leads = (circle) => [...ownerIds(circle)].some((id) => mine.has(id));
  const byId = new Map(circles.map((c) => [String(c.id), c]));
  const roots = [];
  let current = project?.circle_id;
  while (current != null && byId.has(String(current))) {
    const circle = byId.get(String(current));
    if (leads(circle)) roots.push(circle.id);
    current = circle.parent_id;
  }
  if (!roots.length) return [];
  const allowed = new Set();
  const stack = [...roots];
  while (stack.length) {
    const id = stack.pop();
    if (allowed.has(String(id))) continue;
    allowed.add(String(id));
    childrenOf(circles, id).forEach((child) => stack.push(child.id));
  }
  return circles.filter((c) => allowed.has(String(c.id))).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
};
